import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector as useReduxSelector } from "react-redux";
import environment from "web/script/modules/environment";
import { gettext } from "web/script/modules/django-i18n";
import cookie from "web/script/utils/cookie";
import storage from "web/script/utils/storage";
import { EU_COUNTRIES } from "web/react/constants";
import { useSmallBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import browser from "web/script/utils/browser";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import FollowDesignerButton from "web/react/components/follow-designer-button/follow-designer-button";
import styles from "./follow-sticky-banner-compact.css";

const ONE_HOUR = 3600000;

function extractCookieBannerState(state) {
    return state.cookieBannerReducer.cookieIsAccepted;
}

function FollowStickyBannerCompact({
    lystId,
    registrationReason,
    followedText,
    isAuthenticated,
    isFollowing,
    description,
    profileImage,
    forceDisplay,
}) {
    let [show, setShow] = useState(false);
    let [closed, setClosed] = useState(false);
    const wrapperRef = useRef(null);

    const country = environment.get("country");
    const isEu = EU_COUNTRIES.has(country);

    // This makes the banner appear in the bottom of the screen
    const bottomCookieBanner = isEu && environment.getFeature("mg_cookie_banner_always_at_bottom");

    // Redux state to see if cookie is accepted.
    // Will cause re-render when its Accepted
    const cookieIsAccepted = useReduxSelector(extractCookieBannerState);

    // this constant is to check if the cookie is set to true or false
    // when the component is loaded. When redux pushed the accepted state
    // (cookieIsAccepted) we can see that the cookie just been accepted
    const currentCookieState = Boolean(cookie("cookieconsent"));

    // This is to check if to hide the banner when Follow button
    // in the top is visible
    const hideDUBanner =
        !useSmallBreakpoint() && environment.getFeature("mg_hide_du_banner_if_du_cta_in_view");

    // Check if banner should be hidden behind cookiebanner
    const displayWhenCookieDismissed =
        !useSmallBreakpoint() &&
        browser.ios &&
        bottomCookieBanner &&
        environment.getFeature("mg_du_behind_cookiebanner");

    // Check if banner should be visible above the cookie banner
    let aboveCookieBanner = !cookieIsAccepted && bottomCookieBanner && !displayWhenCookieDismissed;

    function displayBanner() {
        setShow(true);
        analytics.event("du_feed_banner", "banner_shown", "du_banner", true); // non interactive
        storage.remove("hideStickyBannerTimestamp");
    }

    const triggerBanner = useCallback((delay) => {
        let hideStickyBannerTimestamp = storage.get("hideStickyBannerTimestamp");
        let timer;
        if (
            !hideStickyBannerTimestamp ||
            hideStickyBannerTimestamp < new Date().getTime() - ONE_HOUR
        ) {
            timer = setTimeout(displayBanner, delay);
        }

        return () => {
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (hideDUBanner || closed) {
            return;
        }

        if (!displayWhenCookieDismissed || currentCookieState) {
            triggerBanner(5000);
        }
    });

    useEffect(() => {
        if (!hideDUBanner || closed) {
            return;
        }

        if (displayWhenCookieDismissed && !currentCookieState) {
            return;
        }

        if (forceDisplay) {
            triggerBanner(500);
        } else {
            setShow(false);
        }
    }, [
        forceDisplay,
        closed,
        triggerBanner,
        displayWhenCookieDismissed,
        currentCookieState,
        hideDUBanner,
    ]);

    useEffect(() => {
        if (closed) {
            return;
        }
        if (displayWhenCookieDismissed && !currentCookieState && cookieIsAccepted) {
            triggerBanner(1000);
        }
    }, [cookieIsAccepted, displayWhenCookieDismissed, currentCookieState, closed, triggerBanner]);

    function triggerClose() {
        setShow(false);
        setClosed(true);
        storage.set("hideStickyBannerTimestamp", new Date().getTime());
        analytics.event("du_feed_banner", "banner_hidden", "du_banner", true); // non interactive
    }

    const bannerClassNames = classnames(styles.stickyBanner, {
        [styles.stickyBannerShow]: show && !closed,
        [styles.aboveCookieBanner]: aboveCookieBanner,
    });

    // "Get designer updates"
    const notFollowedText = gettext("designer_updates.get_designer_updates_btn_text");

    let renderedProfileImage = "";
    if (profileImage) {
        renderedProfileImage = <img src={profileImage} className={styles.imageProfile} />;
    }

    return (
        <div ref={wrapperRef}>
            <div className={bannerClassNames}>
                <div className={styles.wrapper}>
                    <div className={styles.left}>
                        {renderedProfileImage}
                        <div
                            className={styles.content}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                    <div className={styles.right}>
                        <FollowDesignerButton
                            className={styles.button}
                            lystId={lystId}
                            registrationReason={registrationReason}
                            followedText={followedText}
                            notFollowedText={notFollowedText}
                            isAuthenticated={isAuthenticated}
                            isFollowing={isFollowing}
                            description={description}
                            analyticsParameters={[
                                "du_feed_banner",
                                "click_banner_cta",
                                "du_banner",
                            ]}
                        />
                        <button className={styles.closeButton} onClick={triggerClose}>
                            <SVGIcon name="cross" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FollowStickyBannerCompact;

FollowStickyBannerCompact.propTypes = {
    lystId: PropTypes.number,
    registrationReason: PropTypes.string,
    followedText: PropTypes.string,
    notFollowedText: PropTypes.string,
    isAuthenticated: PropTypes.string,
    isFollowing: PropTypes.string,
    description: PropTypes.string,
    profileImage: PropTypes.string,
    forceDisplay: PropTypes.bool,
};
